import { styled, useTheme } from '@mui/material';
import { Logo } from '../../atoms/Logo/Logo';
import { NavUser } from './User';
import { NavLink } from './Link';
import { NavItem, SashLayout } from 'common/types/nav';
import {
    NAV_ACTIONS_GAP,
    NAV_PADDING_LEFT,
    NAV_PADDING_RIGHT,
    NAV_SASH_LABEL,
    NAV_SASH_LABEL_FONT_FAMILY,
    NAV_SASH_TOP
} from './constants';
import { NavContact } from './Contact';
import { useState } from 'react';
import { useAppSelector, useAppDispatch } from '../../../common/hooks';
import dayjs from 'dayjs';
import ellipse from '../../../assets/logos/Hantz_latest_logo.png';
import { Divider, Grid } from '@mui/material';
import { Typography } from '../../atoms';
import { useTranslation } from 'react-i18next';
import { useDropdownState } from '../../../common/hooks/useDropdownState';
import { Dropdown } from '../../../components/atoms/Dropdown';
import { NavTenant } from './tenant';
import LanguageSelectorComponent from '../../organisms/LanguageSelector';
import { ReactComponent as Trial } from '../../../assets/icons/trial.svg';
import { ReactComponent as Demo } from '../../../assets/icons/demo.svg';
import { checkFFEnabled } from '../../../utils';
import { FEATURE_NAMES } from '../../../common/constants';
import NotificationsComponent from '../../../components/organisms/Notifications';
import { updateNotificationReadTimestamp, isNotificationUnread } from '../../../features/global/globalSlice';
import { updateLatestNotificationTimestamp } from '../../../features/auth/authSlice';

const DEMO_TOOLTIP_ID = 'header-demo-tooltip';

const Background = styled('header')(() => ({
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    position: 'fixed',
    zIndex: 100,
    top: 0
}));

const SashComponent = styled(Grid)<{ tenantType: string }>(({ tenantType, theme }) => ({
    display: 'flex',
    color: theme.palette.neutral.white,
    border: `1px solid ${theme.palette.berry[100]}`,
    backgroundColor: tenantType === 'demo' ? theme.palette.warning[600] : theme.palette.berry[400],
    borderRadius: '2px',
    alignItems: 'center',
    cursor: 'pointer'
}));

const Navbar = styled('nav')<{ $sashLayout: SashLayout }>(({ theme, $sashLayout }) => ({
    // Containing block for the corner sash ribbon.
    position: 'relative',
    background: theme.palette.semantic.bg.brand,
    height: '60px',
    paddingLeft: theme.spacing(NAV_PADDING_LEFT[$sashLayout]),
    paddingRight: theme.spacing(NAV_PADDING_RIGHT),
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'nowrap',

    '& > #hantzlogo::before': {
        height: '30px',
        background: 'red',
        width: '40px',
        color: 'red'
    }
}));

const LinksContainer = styled('ul')(() => ({
    display: 'flex',
    flexWrap: 'nowrap',
    gap: '40px',
    alignItems: 'center',
    flex: 1,
    minWidth: 0,
    marginRight: '40px',
    marginLeft: '32px',
    listStyle: 'none',
    padding: 0,
    marginTop: 0,
    marginBottom: 0
}));

const NavListItem = styled('li')(() => ({
    display: 'flex',
    alignItems: 'center'
}));

const NavUtilities = styled('ul')(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'nowrap',
    // The list spaces the actions; the items carry no margins of their own.
    gap: theme.spacing(NAV_ACTIONS_GAP),
    listStyle: 'none',
    margin: 0,
    padding: 0
}));

const ImageContainer = styled('div')(() => ({
    height: '22px',
    width: '120px',
    backgroundImage: `url(${ellipse})`,
    backgroundSize: 'contain',
    backgroundRepeat: 'no-repeat',
    backgroundPositionX: 'right',
    marginLeft: '10px'
}));

// Positioned so both logo variants paint above the corner sash ribbon, whose box overlaps them.
const LogoSlot = styled('div')({ position: 'relative' });

const LogoContainer = styled(LogoSlot)<{ src?: string }>(({ src }) => ({
    height: '32px',
    width: '224px',
    maxWidth: '100%',
    backgroundImage: src ? `url(${src})` : 'none',
    backgroundSize: 'contain',
    backgroundRepeat: 'no-repeat',
    // Left-aligned so every logo shape (wide or square) starts at the same point.
    backgroundPosition: 'left center'
}));

const DefaultLogoContainer = styled(LogoSlot)({ display: 'flex' });

const Content = styled('div')<{
    width?: number;
}>(({ width, theme }) => ({
    width,
    display: 'flex',
    flexDirection: 'column',
    zIndex: 120,
    background: theme.palette.semantic.bg.default,
    border: `1px solid ${theme.palette.semantic.bd.subtle}`,
    borderRadius: '4px',
    boxShadow: '-4px 4px 24px 0px rgba(0, 8, 31, 0.12), 0px 4px 2px 0px rgba(0, 8, 31, 0.04)',
    padding: '20px',
    maxWidth: 'min(312px, calc(100vw - 32px))'
}));

type Props = {
    items: NavItem[];
    user: {
        loggedInUsername: string;
        userRole?: string;
        adminTitle?: string;
    };
    onClickAdmin(): void;
    onClickLogout(): void;
};

// Overlays the header's top-left corner, outside the flex flow.
const SashRibbon = styled('div')(({ theme }) => ({
    position: 'absolute',
    top: theme.spacing(NAV_SASH_TOP),
    left: 0,
    display: 'flex',
    alignItems: 'flex-start',
    lineHeight: 0,
    cursor: 'pointer'
}));

const SashLabel = styled('span')(({ theme }) => ({
    position: 'absolute',
    top: theme.spacing(NAV_SASH_LABEL.top),
    left: theme.spacing(NAV_SASH_LABEL.left),
    width: 0,
    height: 0,
    paddingLeft: theme.spacing(NAV_SASH_LABEL.paddingLeft),
    paddingTop: theme.spacing(NAV_SASH_LABEL.paddingTop),
    transform: `rotate(${NAV_SASH_LABEL.rotation}deg)`,
    // Pivot on the start corner, where the Figma position is anchored, whatever the padding.
    transformOrigin: 'left top',
    color: theme.palette.neutral.white,
    fontFamily: NAV_SASH_LABEL_FONT_FAMILY,
    fontSize: '14px',
    fontStyle: 'normal',
    fontWeight: 700,
    lineHeight: '19px',
    textTransform: 'uppercase',
    whiteSpace: 'nowrap'
}));

export const Nav = ({ items = [], user, onClickAdmin, onClickLogout }: Props) => {
    const theme = useTheme();
    const [activeDropdown, setActiveDropdown] = useState<DropdownNames>();
    const tenant = useAppSelector((state) => state?.auth?.user?.tenant);
    const userRole = useAppSelector((state) => state?.auth?.user?.userRole);
    const { globalConfig } = useAppSelector((state) => state.global);
    const notifications = useAppSelector((state) => state.global.notifications);
    const { loading: authLoading, isAuthenticated } = useAppSelector((state) => state.auth);
    const { t } = useTranslation();
    const { toggle, setAnchorEl, ...dropdownProps } = useDropdownState({});
    const isHantzTenant = useAppSelector((state) => state?.auth?.user?.isHantzTenant);
    const logo = globalConfig?.logo;
    const brandName = globalConfig?.disclaimers?.brandName;
    const isFranklinAdviceTenant = globalConfig?.subdomain === 'advice';
    const sashRevampEnabled = checkFFEnabled(FEATURE_NAMES['SASH_REVAMP_FE_AEX-3480']);
    const sashTenantType = globalConfig?.tenantType?.toLowerCase();
    const isTrialOrDemoTenant = sashTenantType === 'trial' || sashTenantType === 'demo';
    const shouldShowSash = isTrialOrDemoTenant && !isHantzTenant;
    const sashLayout: SashLayout = shouldShowSash ? (sashRevampEnabled ? 'ribbon' : 'legacy') : 'none';
    const SashIcon = sashTenantType === 'demo' ? Demo : Trial;
    const onToggleDropdown = (name: DropdownNames) =>
        setActiveDropdown((oldName) => (oldName === name ? undefined : name));
    const notificationEnabled = checkFFEnabled(FEATURE_NAMES['NOTIFICATION_POPUP_FE_AEX-3213']);

    const dispatch = useAppDispatch();
    const latestNotificationTimestamp = useAppSelector(
        (state) => state?.auth?.user?.cwa?.[tenant]?.latestNotificationTimestamp
    );
    // Mark notifications as seen when the advisor opens the panel, not on login, so the
    // unread dot reflects "viewed" and clears in the same session.
    const handleNotificationsToggle = () => {
        const willOpen = activeDropdown !== 'notifications';
        onToggleDropdown('notifications');
        const hasUnread = notifications.some((n) => isNotificationUnread(n, latestNotificationTimestamp));
        if (willOpen && hasUnread) {
            const timestamp = dayjs().toISOString();
            dispatch(updateNotificationReadTimestamp({ from_tenant: tenant, latestNotificationTimestamp: timestamp }));
            dispatch(updateLatestNotificationTimestamp({ tenant, timestamp }));
        }
    };

    const decideConditionalUiForLogo = () => {
        if (!isFranklinAdviceTenant && globalConfig?.logo) {
            return <LogoContainer src={logo} role="img" aria-label={brandName || t('NAV_LOGO_ALT')} />;
        }
        return (
            <DefaultLogoContainer>
                <Logo />
            </DefaultLogoContainer>
        );
    };

    if (!isAuthenticated)
        return (
            <Background>
                <Navbar $sashLayout="none">{decideConditionalUiForLogo()}</Navbar>
            </Background>
        );

    return (
        <Background>
            <Navbar $sashLayout={sashLayout}>
                {sashLayout === 'ribbon' && (
                    <SashRibbon
                        role="button"
                        tabIndex={0}
                        aria-describedby={DEMO_TOOLTIP_ID}
                        onMouseOver={toggle}
                        onFocus={(e) => setAnchorEl(e.currentTarget)}
                        onBlur={() => setAnchorEl(null)}
                        onMouseLeave={() => {
                            toggle();
                        }}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                setAnchorEl(e.currentTarget);
                            } else if (e.key === 'Escape') {
                                setAnchorEl(null);
                            }
                        }}
                    >
                        <SashIcon aria-hidden focusable={false} />
                        <SashLabel>{sashTenantType.toUpperCase()}</SashLabel>
                        <Dropdown placement="bottom-start" {...dropdownProps}>
                            <Content id={DEMO_TOOLTIP_ID} role="tooltip">
                                <Typography
                                    label={t('SASH_TRAIL_AND_DEMO_TEXT', {
                                        watermarkText: sashTenantType
                                    })}
                                    variant="labels.smallDemibold"
                                />
                            </Content>
                        </Dropdown>
                    </SashRibbon>
                )}
                {decideConditionalUiForLogo()}
                {isHantzTenant ? (
                    <>
                        <Divider
                            style={{ marginLeft: '10px', color: 'rgba(0, 0, 0, 0.5)', borderColor: 'snow' }}
                            orientation="vertical"
                            variant="middle"
                            flexItem
                        />{' '}
                        <ImageContainer id="hantzlogo" />
                    </>
                ) : null}
                {sashLayout === 'legacy' && !authLoading && (
                    <SashComponent
                        role="button"
                        tabIndex={0}
                        aria-describedby={DEMO_TOOLTIP_ID}
                        onMouseOver={toggle}
                        onFocus={(e) => setAnchorEl(e.currentTarget)}
                        onBlur={() => setAnchorEl(null)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                setAnchorEl(e.currentTarget);
                            } else if (e.key === 'Escape') {
                                setAnchorEl(null);
                            }
                        }}
                        tenantType={sashTenantType || ''}
                        onMouseLeave={() => {
                            toggle();
                        }}
                        display="flex"
                        marginLeft={3}
                        gap="5px"
                        px={1}
                        paddingTop="2px"
                    >
                        <Typography
                            sx={{ color: theme.palette.neutral.white }}
                            variant="body.small"
                            label={(sashTenantType || '').toLocaleUpperCase()}
                        />
                        <Dropdown placement={'bottom-start'} {...dropdownProps}>
                            <Content id={DEMO_TOOLTIP_ID} role="tooltip">
                                <Typography
                                    label={t('SASH_TRAIL_AND_DEMO_TEXT', {
                                        watermarkText: sashTenantType
                                    })}
                                    variant="labels.smallDemibold"
                                />
                            </Content>
                        </Dropdown>
                    </SashComponent>
                )}

                <LinksContainer>
                    {isHantzTenant
                        ? null
                        : items.map(({ label, ...item }, index) => (
                              <NavListItem key={`${label}_${index}`}>
                                  <NavLink {...item}>{label}</NavLink>
                              </NavListItem>
                          ))}
                </LinksContainer>
                <NavUtilities>
                    {notificationEnabled && (
                        <NavListItem>
                            <NotificationsComponent
                                onToggleDropdown={handleNotificationsToggle}
                                isDropdownActive={activeDropdown === 'notifications'}
                                notifications={notifications}
                            />
                        </NavListItem>
                    )}
                    <NavListItem>
                        <LanguageSelectorComponent
                            onToggleDropdown={() => onToggleDropdown('language')}
                            isDropdownActive={activeDropdown === 'language'}
                        />
                    </NavListItem>
                    {userRole && userRole.toLowerCase() === 'ftadmin' && (
                        <NavListItem>
                            <NavTenant
                                currentTenant={tenant}
                                onToggleDropdown={() => onToggleDropdown('tenant')}
                                isDropdownActive={activeDropdown === 'tenant'}
                            />
                        </NavListItem>
                    )}
                    <NavListItem>
                        <NavUser
                            {...user}
                            tenant={tenant}
                            isHantzTenant={isHantzTenant}
                            isDropdownActive={activeDropdown === 'user'}
                            onToggleDropdown={() => onToggleDropdown('user')}
                            onClickAdmin={onClickAdmin}
                            onClickLogout={onClickLogout}
                        />
                    </NavListItem>
                    <NavListItem>
                        <NavContact
                            isDropdownActive={activeDropdown === 'contact'}
                            onToggleDropdown={() => onToggleDropdown('contact')}
                        />
                    </NavListItem>
                </NavUtilities>
            </Navbar>
        </Background>
    );
};

type DropdownNames = 'user' | 'contact' | 'tenant' | 'language' | 'notifications';
